import { DBRecord, StaticPage } from '../types';

const STORE = '360digi_v2';

function getDB(): Record<string, Record<string, DBRecord[]>> {
  try {
    return JSON.parse(localStorage.getItem(STORE) || '{}');
  } catch {
    return {};
  }
}

function saveDB(db: Record<string, Record<string, DBRecord[]>>): void {
  localStorage.setItem(STORE, JSON.stringify(db));
}

function todayKey(): string {
  return new Date().toISOString().split('T')[0];
}

export function saveRecord<T extends Record<string, unknown>>(folder: string, data: T): T & DBRecord {
  const db = getDB();
  const d = todayKey();
  if (!db[d]) db[d] = {};
  if (!db[d][folder]) db[d][folder] = [];
  const record = {
    ...data,
    _id: 'r_' + Date.now(),
    _timestamp: new Date().toISOString(),
    _date: d,
  } as T & DBRecord;
  db[d][folder].push(record as DBRecord);
  saveDB(db);
  return record;
}

export function getAllRecords(folder: string): DBRecord[] {
  const db = getDB();
  const all: DBRecord[] = [];
  Object.keys(db)
    .sort((a, b) => b.localeCompare(a))
    .forEach(date => {
      (db[date][folder] || []).forEach(r => all.push(r));
    });
  return all;
}

export function getFullDB(): Record<string, Record<string, DBRecord[]>> {
  return getDB();
}

export function clearDB(): void {
  localStorage.removeItem(STORE);
}

export function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  );
}

export function exportJSON(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── Exam Questions Storage ──────────────────────────────────────────────

const EXAM_QUESTIONS_KEY = '360digi_exam_questions';
const EXAM_CATEGORIES_KEY = '360digi_exam_categories';

export interface StoredQuestion {
  id: string;
  examId?: string;
  categoryId: string;
  question: string;
  options: string[];
  correctAnswer: number | number[];
  explanation: string;
}

export interface StoredCategory {
  id: string;
  name: string;
  emoji: string;
}

export function saveQuestionsToStorage(questions: StoredQuestion[]): void {
  localStorage.setItem(EXAM_QUESTIONS_KEY, JSON.stringify(questions));
}

export function getQuestionsFromStorage(): StoredQuestion[] {
  try {
    const data = localStorage.getItem(EXAM_QUESTIONS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function saveCategoriesToStorage(categories: StoredCategory[]): void {
  localStorage.setItem(EXAM_CATEGORIES_KEY, JSON.stringify(categories));
}

export function getCategoriesFromStorage(): StoredCategory[] {
  try {
    const data = localStorage.getItem(EXAM_CATEGORIES_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

// ─── Static HTML Pages Storage ───────────────────────────────────────────

const STATIC_PAGES_KEY = '360digi_static_pages';

export function saveStaticPage(page: StaticPage): StaticPage {
  const pages = getStaticPagesFromStorage();
  const existingIndex = pages.findIndex(p => p.id === page.id);
  
  if (existingIndex >= 0) {
    pages[existingIndex] = { ...page, updated_at: new Date().toISOString() };
  } else {
    pages.push({
      ...page,
      id: page.id || 'page_' + Date.now(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }
  
  localStorage.setItem(STATIC_PAGES_KEY, JSON.stringify(pages));
  return pages[existingIndex >= 0 ? existingIndex : pages.length - 1];
}

export function getStaticPagesFromStorage(): StaticPage[] {
  try {
    const data = localStorage.getItem(STATIC_PAGES_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export function getStaticPageBySlug(slug: string): StaticPage | null {
  const pages = getStaticPagesFromStorage();
  return pages.find(p => p.slug === slug && p.is_published) || null;
}

export function getStaticPageById(id: string): StaticPage | null {
  const pages = getStaticPagesFromStorage();
  return pages.find(p => p.id === id) || null;
}

export function deleteStaticPage(id: string): void {
  const pages = getStaticPagesFromStorage().filter(p => p.id !== id);
  localStorage.setItem(STATIC_PAGES_KEY, JSON.stringify(pages));
}
